import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, MessageCircle, Phone } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import PinChecker from "../components/PinChecker";
import { applianceMeta, businessInfo, whyChoose } from "../constants";
import { getServices } from "../api";

export default function ApplianceService({ slug }) {
  const meta = applianceMeta[slug];
  const [services, setServices] = useState([]);
  const nav = useNavigate();

  useEffect(() => { getServices(slug).then(setServices).catch(() => {}); }, [slug]);

  if (!meta) return null;

  return (
    <Layout>
      <SEO title={meta.seoTitle} description={meta.seoDesc} canonical={meta.route} />

      <section className="relative bg-[#0b1220] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 kenburns"><img src={meta.hero} alt={`Elica ${meta.title}`} className="w-full h-full object-cover" /></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220] via-[#0b1220]/85 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-24">
          <nav className="text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-white">Home</Link> / <span className="text-white">Elica {meta.title} Service</span>
          </nav>
          <div className="chip chip-dark mb-5">Authorised Elica Service</div>
          <h1 className="font-display text-4xl md:text-6xl font-medium leading-[1.05] mb-5 max-w-3xl">
            Elica {meta.title} Service &amp; Repair in Pune
          </h1>
          <p className="text-lg text-white/75 mb-8 max-w-2xl">{meta.tagline}. Doorstep service by trained technicians with genuine Elica spare parts.</p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => nav(`/book-service?appliance=${slug}`)} className="btn-primary">Book {meta.title} Service <ArrowRight className="w-4 h-4" /></button>
            <a href={businessInfo.whatsapp} target="_blank" rel="noreferrer" className="btn-outline-light"><MessageCircle className="w-4 h-4" /> WhatsApp Us</a>
          </div>
        </div>
      </section>

      {/* Services list + PIN checker */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_380px] gap-12">
          <div>
            <div className="chip mb-4">Available Services</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium mb-3">Everything we service on your Elica {meta.title}</h2>
            <p className="text-slate-500 mb-8 max-w-xl">Pick the service that matches your need. Not sure? Book a diagnostic visit – our technician will inspect and share an estimate before any repair begins.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {services.map((s) => (
                <button key={s.id} onClick={() => nav(`/book-service?appliance=${slug}&service=${encodeURIComponent(s.title)}`)} className="text-left p-5 rounded-lg border border-neutral-200 hover-card bg-white">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-md bg-[#fef2f2] grid place-items-center flex-shrink-0"><Check className="w-4 h-4 text-[#e30613]" /></div>
                    <div>
                      <div className="font-semibold text-sm mb-0.5">{s.title}</div>
                      <div className="text-xs text-slate-500">Book this service</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <aside className="space-y-4">
            <PinChecker autoRedirect />
            <div className="bg-[#0b1220] text-white rounded-lg p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">Need help now?</div>
              <div className="font-display text-xl mb-4">Talk to us</div>
              <div className="space-y-2 text-sm">
                <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2 hover:text-[#e30613] transition-colors"><Phone className="w-4 h-4" /> {businessInfo.phoneDisplay}</a>
                <a href={businessInfo.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#25D366] transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp {businessInfo.phoneDisplay}</a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Why us */}
      <section className="py-16 section-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="chip mb-4">Why Revive Solutions</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium leading-tight">A dedicated Elica service you can trust</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {whyChoose.slice(0, 6).map((w) => (
              <div key={w.title} className="p-6 rounded-lg border border-neutral-200 bg-white hover-card">
                <h3 className="font-semibold text-[15px] mb-2">{w.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-dark py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-medium mb-4">Book your {meta.title} service today</h2>
          <p className="text-white/70 mb-7">Same-day slots available for most Pune PIN codes.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => nav(`/book-service?appliance=${slug}`)} className="btn-primary">Book Now <ArrowRight className="w-4 h-4" /></button>
            <a href={businessInfo.whatsapp} target="_blank" rel="noreferrer" className="btn-outline-light"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
